import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { supabase } from "../../../../supaBaseClient"
import { API_BASE } from "../../../../config";

// use centralized API_BASE from config

// Turn any pasted URL into a storage object path (no domain, no bucket, no leading slash)
const toObjectPath = (value) => {
  if (!value) return value;
  let v = value.trim();

  // Strip any accidental bucket prefixes
  v = v.replace(/^exam[_-]?questions\//i, ""); // exam_questions
  v = v.replace(/^exam[_-]?solutions\//i, ""); // exam_solutions

  // Remove leading slashes
  v = v.replace(/^\/+/, "");
  return v;
};

const AdminQuestions = ({ topicId, onClose, onUpdate }) => {
  const [examQuestionsUrl, setExamQuestionsUrl] = useState("");
  const [solutionUrl, setSolutionUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [signedExamUrl, setSignedExamUrl] = useState(null);
  const [signedSolutionUrl, setSignedSolutionUrl] = useState(null);
  const [userId, setUserId] = useState(null);

  // Fetch user ID from Supabase
  useEffect(() => {
    let unsub = () => {};
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) setUserId(data.user.id);
      else setUserId(null);

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        const user = session?.user;
        setUserId(user ? user.id : null);
      });

      unsub = () => subscription.unsubscribe();
    })();
    return () => unsub();
  }, []);

  // Fetch the current URLs when the modal opens
  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/topics/${topicId}`);
        setExamQuestionsUrl(response.data.exam_questions_url || "");
        setSolutionUrl(response.data.solution_url || "");
      } catch (err) {
        console.error("Error fetching topic details:", err);
        toast.error("Failed to load topic details.");
      }
    };
    if (topicId) fetchTopicData();
  }, [topicId]);

  // Save the updated Exam Questions & Solution PDFs
  const handleSaveChanges = async () => {
    if (!examQuestionsUrl || !solutionUrl) {
      toast.error("Both Exam Questions and Solution paths are required.");
      return;
    }

    if (!window.confirm("Are you sure you want to save these changes?")) return;

    setIsSaving(true);
    try {
      // Normalize to object path (no bucket/domain)
      const qsPath = toObjectPath(examQuestionsUrl);
      const solPath = toObjectPath(solutionUrl);

      // Update question path (stored in topics.exam_questions_url)
      await axios.post(`${API_BASE}/api/topics/topic-questions`, {
        topic_id: topicId,
        file_url: qsPath,
      });

      // Update solution path (stored in topics.solution_url)
      await axios.post(`${API_BASE}/api/topics/topic-solutions`, {
        topic_id: topicId,
        file_url: solPath,
      });

      toast.success("Exam questions & solutions updated successfully.");

      if (typeof onUpdate === "function") onUpdate();
      onClose();
    } catch (err) {
      console.error("Error updating topic questions/solutions:", err);
      toast.error(err.response?.data?.error || "Failed to update.");
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch signed URL for Exam Questions (logs access)
  const handleViewExamPdf = async () => {
    if (!examQuestionsUrl) {
      toast.error("No Exam PDF stored for this topic.");
      return;
    }
    if (!userId) {
      toast.error("User not logged in.");
      return;
    }

    try {
      const qsPath = toObjectPath(examQuestionsUrl);
      const response = await axios.get(`${API_BASE}/api/topics/signed-url`, {
        params: {
          bucket_type: "topic_questions",
          file_path: qsPath,
          user_uid: userId,
          topic_id: topicId,
        },
      });
      setSignedExamUrl(response.data.signedUrl);
    } catch (err) {
      console.error("Error fetching signed URL:", err);
      toast.error(err.response?.data?.error || "Failed to fetch signed URL.");
    }
  };

  // Fetch signed URL for Solutions (no user needed)
  const handleViewSolutionPdf = async () => {
    if (!solutionUrl) {
      toast.error("No Solution PDF stored for this topic.");
      return;
    }

    try {
      const solPath = toObjectPath(solutionUrl);
      const response = await axios.get(`${API_BASE}/api/topics/signed-url`, {
        params: {
          bucket_type: "topic_solutions",
          file_path: solPath,
        },
      });
      setSignedSolutionUrl(response.data.signedUrl);
    } catch (err) {
      console.error("Error fetching signed URL:", err);
      toast.error(err.response?.data?.error || "Failed to fetch signed URL.");
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xl">
        <h2 className="text-xl font-bold mb-4">Manage Exam Questions & Solutions</h2>

        {/* Exam Questions */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700">
            Exam Questions Path (inside <code>exam_questions</code>):
          </label>
          <input
            type="text"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            value={examQuestionsUrl}
            onChange={(e) => setExamQuestionsUrl(e.target.value)}
            placeholder="e.g. Grade1/Number/Addition/qs.pdf"
          />
          <div className="flex justify-between mt-2">
            <button
              onClick={handleViewExamPdf}
              className="text-green-600 font-medium hover:underline"
            >
              View Exam PDF
            </button>
          </div>
        </div>

        {/* Solutions */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700">
            Solution Path (inside <code>exam_solutions</code>):
          </label>
          <input
            type="text"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            value={solutionUrl}
            onChange={(e) => setSolutionUrl(e.target.value)}
            placeholder="e.g. Grade1/Number/Addition/sol.pdf"
          />
          <div className="flex justify-between mt-2">
            <button
              onClick={handleViewSolutionPdf}
              className="text-green-600 font-medium hover:underline"
            >
              View Solution PDF
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* PDF Viewer Modal: Exam */}
      {signedExamUrl && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-5 rounded-lg shadow-lg max-w-2xl w-full">
            <h2 className="text-xl font-semibold mb-4">Exam PDF Viewer</h2>
            <iframe title="Exam PDF" src={signedExamUrl} className="w-full h-96 border rounded-md" />
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setSignedExamUrl(null)}
                className="px-4 py-2 bg-red-500 text-white rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal: Solution */}
      {signedSolutionUrl && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-5 rounded-lg shadow-lg max-w-2xl w-full">
            <h2 className="text-xl font-semibold mb-4">Solution PDF Viewer</h2>
            <iframe title="Solution PDF" src={signedSolutionUrl} className="w-full h-96 border rounded-md" />
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setSignedSolutionUrl(null)}
                className="px-4 py-2 bg-red-500 text-white rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions;